// The cards on a store page, in the shape public Minecraft stores use: art on top, the name and one line about it, the
// price, and an "Add to cart" button on the card itself. The whole card still opens the item's dialog (the title is the link).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../../icons";
import { Badge, errorMessage } from "../../ui";
import { PriceTag } from "./Price";
import { useStore } from "./StoreSession";
import type { Currency, StoreCatalog, StoreCategory, StoreItem } from "./storeSite";
import { ItemArt } from "./ItemArt";
import { endsIn, fromPrice, pathOf, perksOf, savePercent, stockLine, summaryOf, tileOf, typeLine } from "./storeText";

export function Tile({ icon, type, size = 22 }: { icon: string | null; type: string | null; size?: number }) {
  const tile = tileOf(icon, type);
  return <span className="tile-icon store-tile" aria-hidden="true">{"name" in tile ? <Icon name={tile.name} size={size}/> : <span className="store-tile-text">{tile.text}</span>}</span>;
}

// "Add to cart" without opening anything first, when there is nothing to choose: one way to pay and an account to deliver to.
function CardAction({ item, href, labelledBy }: { item: StoreItem; href: string; labelledBy: string }) {
  const store = useStore();
  const [phase, setPhase] = useState<"idle" | "busy" | "added">("idle");
  const [error, setError] = useState("");
  useEffect(() => {
    if (phase !== "added") return;
    const timer = window.setTimeout(() => setPhase("idle"), 2400);
    return () => window.clearTimeout(timer);
  }, [phase]);

  const open = item.offers.filter((offer) => offer.available);
  if (open.length === 0) return null;
  if (!store.signedIn) return <Link className="button compact pkg-action" to={href} aria-describedby={labelledBy}>View details</Link>;
  if (!store.selected) return <button type="button" className="button compact pkg-action" aria-haspopup="dialog" aria-describedby={labelledBy} onClick={store.openAccount}><Icon name="link" size={16}/>Link account to buy</button>;
  if (open.length > 1) return <Link className="button compact pkg-action" to={href} aria-describedby={labelledBy}>Choose how to pay</Link>;

  const offer = open[0];
  const add = async (currency: Currency) => {
    setPhase("busy"); setError("");
    try { await store.add(item.id, currency, 1); setPhase("added"); }
    catch (reason) { setError(errorMessage(reason, "That could not be added to your cart. Try again.")); setPhase("idle"); }
  };
  return <>
    <button type="button" className={`button compact pkg-action ${phase === "added" ? "is-added" : "primary"}`} disabled={phase === "busy"} aria-describedby={labelledBy} onClick={() => add(offer.currency)}>
      <Icon name={phase === "added" ? "check" : "cart"} size={16}/>{phase === "busy" ? "Adding…" : phase === "added" ? "Added" : "Add to cart"}
    </button>
    {error && <p className="field-error pkg-error" role="alert">{error}</p>}
  </>;
}

export function ItemCard({ item, href, variant = "card", previous }: { item: StoreItem; href: string; variant?: "card" | "tier"; previous?: StoreItem }) {
  const stock = stockLine(item);
  const sales = item.offers.filter((offer) => offer.saleActive);
  const endings = sales.map((offer) => offer.saleEndsAt).filter((value): value is string => Boolean(value)).sort();
  const upgrade = item.offers.find((offer) => offer.upgradeFrom)?.upgradeFrom;
  const blocked = item.offers.length > 0 && item.offers.every((offer) => !offer.available) ? item.offers[0].reasonText : "";
  const save = savePercent(item);
  const nameId = `item-${item.id}-name`;
  const perks = variant === "tier" ? perksOf(item.descriptionMd) : [];
  const summary = variant === "tier" && perks.length > 0 ? "" : summaryOf(item.descriptionMd);
  return <li className={`store-card pkg ${variant === "tier" ? "tier" : ""} ${blocked ? "is-blocked" : ""}`}>
    <ItemArt item={item} size={variant === "tier" ? "wide" : "card"}/>
    <div className="pkg-body">
      <p className="store-kicker">{typeLine(item)}</p>
      <h3 id={nameId}><Link className="stretched" to={href}>{item.name}</Link></h3>
      {summary && <p className="pkg-summary">{summary}</p>}
      {variant === "tier" && <>
        {previous && <p className="pkg-includes">Includes everything in {previous.name}</p>}
        {perks.length > 0 && <ul className="pkg-perks">{perks.map((perk) => <li key={perk}><Icon name="check" size={16}/><span>{perk}</span></li>)}</ul>}
      </>}
    </div>
    <div className="pkg-foot">
      <ul className="store-prices" aria-label="Prices">{item.offers.map((offer) => <li key={offer.currency}><PriceTag currency={offer.currency} amount={offer.unitPrice} was={offer.saleActive ? offer.listPrice : null}/></li>)}</ul>
      {(save > 0 || upgrade || stock || item.perAccountLimit != null) && <p className="store-badges">
        {save > 0 && <Badge tone="accent">Save {save}%</Badge>}
        {upgrade && <Badge tone="info">Upgrade from {upgrade.name}</Badge>}
        {stock && <Badge tone={item.remainingStock === 0 ? "danger" : "warning"}>{stock}</Badge>}
        {item.perAccountLimit != null && <Badge tone="neutral">Limit {item.perAccountLimit} per account</Badge>}
      </p>}
      {endings.length > 0 && <p className="store-fine">{endsIn(endings[0])}</p>}
      {blocked && <p className="store-blocked">{blocked}</p>}
      <CardAction item={item} href={href} labelledBy={nameId}/>
    </div>
  </li>;
}

export function CategoryCard({ category, chain, catalog }: { category: StoreCategory; chain: StoreCategory[]; catalog: StoreCatalog }) {
  const from = fromPrice(catalog.categories, catalog.items, category.id);
  return <li className="store-card category-card">
    <ItemArt item={{ icon: category.icon, type: "category", imageUrl: null }} size="tile"/>
    <div className="category-card-body">
      <h3><Link className="stretched" to={pathOf([...chain, category])}>{category.name}</Link></h3>
      {category.description && <p>{category.description}</p>}
      <span className="store-count">{category.itemCount} {category.itemCount === 1 ? "item" : "items"}{from && <> <span className="store-from">from {new Intl.NumberFormat().format(from.amount)} {from.currency === "gems" ? "Gems" : "Orbs"}</span></>}<Icon name="arrowRight" size={16}/></span>
    </div>
  </li>;
}
